import { GridColDef } from '@mui/x-data-grid';
import { GridInitialStateCommunity } from '@mui/x-data-grid/models/gridStateCommunity';
import dayjs from 'dayjs';
import { default as utc } from 'dayjs/plugin/utc';
import { FC, PropsWithChildren, createContext, useContext, useState } from 'react';

import { DataTableColumn } from 'components/tables/data-table';
import { BillableEvent, BillableEventType, ListContextType } from 'types';

dayjs.extend(utc);

const columnDefs: GridColDef<BillableEvent>[] = [
  {
    field: 'timestamp',
    headerName: 'Date',
    type: 'date',
    width: 140,
    valueGetter: ({ value }) => (!value ? null : new Date(value)),
    valueFormatter: ({ value }) => (!value ? null : dayjs(value).utc().format('M/D/YYYY'))
  },
  {
    field: 'type',
    headerName: 'Type',
    valueFormatter: ({ value }) => {
      switch (value as BillableEventType) {
        case BillableEventType.LIFT_OFF:
          return 'Lift Off';
        case BillableEventType.LIFT_ON:
          return 'Lift On';
        case BillableEventType.DIG:
          return 'Dig';
        case BillableEventType.CREDIT:
          return 'Credit';
        case BillableEventType.OTHER:
        default:
          return 'Other';
      }
    }
  },
  {
    field: 'unitNumber',
    headerName: 'Unit',
    width: 200
  },
  {
    field: 'cost',
    headerName: 'Cost',
    type: 'number',
    width: 125,
    headerAlign: 'left',
    align: 'left',
    valueFormatter: ({ value }) => (value as Number).toFixed(2)
  },
  {
    field: 'operator',
    headerName: 'Operator',
    width: 150
  },
  {
    field: 'label',
    headerName: 'Notes',
    width: 300
  }
];

export const billableEventDataColumns: DataTableColumn[] = [
  {
    field: 'timestamp',
    label: 'Date',
    type: 'date',
    width: 140,
    getValueLabel: (v: Date) => (!!v ? dayjs(v).utc().format('M/D/YYYY') : null)
  },
  {
    field: 'type',
    label: 'Type',
    getValueLabel: (value: BillableEventType) => {
      switch (value) {
        case BillableEventType.LIFT_OFF:
          return 'Lift Off';
        case BillableEventType.LIFT_ON:
          return 'Lift On';
        case BillableEventType.DIG:
          return 'Dig';
        case BillableEventType.CREDIT:
          return 'Credit';
        case BillableEventType.OTHER:
        default:
          return 'Other';
      }
    },
    width: 100
  },
  {
    field: 'unitNumber',
    label: 'Unit',
    width: 200
  },
  {
    field: 'cost',
    label: 'Cost',
    type: 'number',
    width: 125,
    getValueLabel: (value: number) => value?.toFixed(2)
  },
  {
    field: 'operator',
    label: 'Operator',
    width: 150
  },
  {
    field: 'label',
    label: 'Notes',
    width: 150
  }
];

const initialVisibility: { [P in keyof Partial<BillableEvent>]: boolean } = {
  timestamp: true,
  type: true,
  label: true,
  unit: true,
  cost: true,
  operator: true
};

const BillableEventListContext = createContext<ListContextType<BillableEvent>>({
  columnDefs: [] as GridColDef<BillableEvent>[],
  columnVisibilityModel: initialVisibility,
  saveList: () => {}
});

export const useBillableEventList = () => useContext(BillableEventListContext);

export const BillableEventListProvider: FC<PropsWithChildren> = ({ children }) => {
  const [billableEventListState, saveBillableEventList] = useState<GridInitialStateCommunity>();

  return (
    <BillableEventListContext.Provider
      value={{
        columnDefs,
        columnVisibilityModel: initialVisibility,
        listState: billableEventListState,
        saveList: saveBillableEventList
      }}
    >
      {children}
    </BillableEventListContext.Provider>
  );
};
